import posthog from "posthog-js";

import type { QuestionnaireValues } from "@/lib/questionnaire-schema";
import type { Recommendation } from "@/lib/recommendations";
import type { SensoryProfileValues } from "@/lib/sensory-profile-schema";
import { normalizePathname, shouldCaptureRoutePageview } from "@/lib/routes";

export type AnalyticsProps = Record<string, string | number>;
export type SymptomAnalyticsAt = "step" | "submit";
export type ProductRecommendationAudience = "client" | "staff";
export type ProductRecommendationSlot = "recommended" | "also_like";

export type AnalyticsEventSpec = {
  event: string;
  props: AnalyticsProps;
};

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

/** Registered at init; inherited on every later event. Query string is not copied to $current_url. */
export const ALLOWLISTED_UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

let initialized = false;

function posthogToken(): string {
  return import.meta.env.VITE_POSTHOG_PROJECT_TOKEN?.trim() ?? "";
}

function posthogHost(): string {
  return import.meta.env.VITE_POSTHOG_HOST?.trim() || DEFAULT_POSTHOG_HOST;
}

/** Test seam: production build + configured PostHog project token. */
export function canCollectAnalytics(options?: {
  isProduction?: boolean;
  token?: string;
}): boolean {
  const isProduction = options?.isProduction ?? import.meta.env.PROD;
  const token = (options?.token ?? posthogToken()).trim();
  return isProduction && token.length > 0;
}

/** Keys that must never appear in analytics payloads. */
export const FORBIDDEN_ANALYTICS_KEYS = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "dateOfBirth",
  "childDateOfBirth",
  "additionalSymptoms",
  "additionalOutcomes",
  "additionalAllergies",
  "additionalMedicalConditions",
  "additionalDiet",
  "additionalExercise",
  "additionalSleepHabits",
  "additionalLifestyleFactors",
  "additionalScreening",
  "currentSupplements",
  "currentMedications",
  "familyMedicalHistory",
  "childNickname",
  "additionalSounds",
  "additionalLightsAndVisualInput",
  "additionalTouchAndTextures",
  "additionalSmells",
  "additionalTastes",
  "additionalMovement",
  "additionalBodyAwareness",
  "additionalEnvironmental",
  "additionalEarlySigns",
  "additionalWhatHelps",
  "triggerOne",
  "triggerTwo",
  "triggerThree",
  "earliestSigns",
  "whatUsuallyHelps",
  "overloadPlan",
] as const;

export function isAnalyticsEnabled(): boolean {
  return canCollectAnalytics();
}

export function getPosthogClient(): typeof posthog {
  return posthog;
}

export function captureRoutePageview(pathname: string): void {
  if (!shouldCaptureRoutePageview(pathname)) return;
  pageview(normalizePathname(pathname));
}

export function parseAllowlistedUtmParams(search: string): AnalyticsProps {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(normalized);
  const props: AnalyticsProps = {};
  for (const key of ALLOWLISTED_UTM_PARAMS) {
    const value = params.get(key)?.trim();
    if (value) props[key] = value;
  }
  return props;
}

export function registerAllowlistedUtmParams(
  search: string,
  registerImpl: (props: AnalyticsProps) => void = (props) => {
    posthog.register(props);
  },
): void {
  const props = parseAllowlistedUtmParams(search);
  if (Object.keys(props).length > 0) {
    registerImpl(props);
  }
}

function joinIds(ids: readonly string[]): string {
  return ids.join(",");
}

function hasNotes(value: string): 0 | 1 {
  return value.trim().length > 0 ? 1 : 0;
}

function countFamilyHistorySelections(
  history: QuestionnaireValues["familyMedicalHistory"],
): number {
  let count = 0;
  for (const relations of Object.values(history)) {
    for (const checked of Object.values(relations)) {
      if (checked) count += 1;
    }
  }
  return count;
}

export function buildIntakeSubmitProps(
  values: QuestionnaireValues,
): AnalyticsProps {
  return {
    symptoms_count: values.selectedSymptoms.length,
    outcomes_count: values.selectedOutcomes.length,
    allergies_count: values.selectedAllergies.length,
    conditions_count: values.selectedMedicalConditions.length,
    diets_count: values.selectedDiets.length,
    exercise_count: values.selectedExercise.length,
    sleep_habits_count: values.selectedSleepHabits.length,
    lifestyle_factors_count: values.selectedLifestyleFactors.length,
    screening_count: values.selectedScreening.length,
    supplements_count: values.currentSupplements.length,
    medications_count: values.currentMedications.length,
    family_history_count: countFamilyHistorySelections(
      values.familyMedicalHistory,
    ),
    symptoms: joinIds(values.selectedSymptoms),
    outcomes: joinIds(values.selectedOutcomes),
    allergies: joinIds(values.selectedAllergies),
    conditions: joinIds(values.selectedMedicalConditions),
    diets: joinIds(values.selectedDiets),
    exercise: joinIds(values.selectedExercise),
    sleep_habits: joinIds(values.selectedSleepHabits),
    lifestyle_factors: joinIds(values.selectedLifestyleFactors),
    screening: joinIds(values.selectedScreening),
    has_notes_symptoms: hasNotes(values.additionalSymptoms),
    has_notes_outcomes: hasNotes(values.additionalOutcomes),
    has_notes_allergies: hasNotes(values.additionalAllergies),
    has_notes_conditions: hasNotes(values.additionalMedicalConditions),
    has_notes_diet: hasNotes(values.additionalDiet),
    has_notes_exercise: hasNotes(values.additionalExercise),
    has_notes_sleep: hasNotes(values.additionalSleepHabits),
    has_notes_lifestyle: hasNotes(values.additionalLifestyleFactors),
    has_notes_screening: hasNotes(values.additionalScreening),
  };
}

export function buildSensoryProfileSubmitProps(
  values: SensoryProfileValues,
): AnalyticsProps {
  return {
    sounds_count: values.selectedSounds.length,
    lights_count: values.selectedLightsAndVisualInput.length,
    touch_count: values.selectedTouchAndTextures.length,
    smells_count: values.selectedSmells.length,
    tastes_count: values.selectedTastes.length,
    movement_count: values.selectedMovement.length,
    body_awareness_count: values.selectedBodyAwareness.length,
    environmental_count: values.selectedEnvironmental.length,
    early_signs_count: values.selectedEarlySigns.length,
    what_helps_count: values.selectedWhatHelps.length,
    rating_sounds: values.sensitivityRatings.sounds,
    rating_lights: values.sensitivityRatings.lightsAndVisualInput,
    rating_touch: values.sensitivityRatings.touchAndTextures,
    rating_smells: values.sensitivityRatings.smells,
    rating_tastes: values.sensitivityRatings.tastes,
    rating_movement: values.sensitivityRatings.movement,
    rating_body_awareness: values.sensitivityRatings.bodyAwareness,
    rating_environmental: values.sensitivityRatings.environmental,
    has_notes_sounds: hasNotes(values.additionalSounds),
    has_notes_plan: hasNotes(values.overloadPlan),
  };
}

export function assertSafeAnalyticsProps(props: AnalyticsProps): void {
  for (const key of Object.keys(props)) {
    if (
      FORBIDDEN_ANALYTICS_KEYS.includes(
        key as (typeof FORBIDDEN_ANALYTICS_KEYS)[number],
      )
    ) {
      throw new Error(`Analytics prop "${key}" is not allowed`);
    }
  }
}

function capture(event: string, properties?: AnalyticsProps): void {
  if (!isAnalyticsEnabled()) return;
  if (properties) assertSafeAnalyticsProps(properties);
  posthog.capture(event, properties);
}

export function pageview(path: string): void {
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  capture("$pageview", {
    $current_url: `${origin}${path}`,
    $pathname: path,
    intake_path: path,
  });
}

export function track(event: string, props?: AnalyticsProps): void {
  capture(event, props);
}

export function captureException(error: unknown, extra?: AnalyticsProps): void {
  if (!isAnalyticsEnabled()) return;
  if (extra) assertSafeAnalyticsProps(extra);
  posthog.captureException(error, extra);
}

export function buildIntakeSubmissionStoreErrorProps(error: {
  code?: string;
  message: string;
  stage?: "draft" | "submit";
}): AnalyticsProps {
  return {
    error_code: error.code ?? "unknown",
    error_message: error.message.slice(0, 200),
    ...(error.stage ? { stage: error.stage } : {}),
  };
}

export function trackIntakeSubmissionStoreError(error: {
  code?: string;
  message: string;
  stage?: "draft" | "submit";
}): void {
  track(
    "intake_submit_store_error",
    buildIntakeSubmissionStoreErrorProps(error),
  );
}

export function trackIntakeStoreOk(): void {
  track("intake_store_ok", { stage: "submit" });
}

export function explodeSymptomEvents(
  ids: readonly string[],
  at: SymptomAnalyticsAt,
): AnalyticsEventSpec[] {
  return ids.map((symptom_id) => ({
    event: "intake_symptom",
    props: { symptom_id, at },
  }));
}

export function trackSymptoms(
  ids: readonly string[],
  at: SymptomAnalyticsAt,
): void {
  for (const { event, props } of explodeSymptomEvents(ids, at)) {
    track(event, props);
  }
}

export function buildProductImpressionProps(
  recommendation: Recommendation,
  slot: ProductRecommendationSlot,
  rank: number,
  audience: ProductRecommendationAudience,
): AnalyticsProps {
  return {
    product_id: recommendation.product.id,
    slot,
    rank,
    audience,
    score: recommendation.score,
    source: recommendation.source ?? "keyword",
  };
}

export function trackProductImpressions(
  recommendations: readonly Recommendation[],
  alsoLike: readonly Recommendation[],
  audience: ProductRecommendationAudience,
): void {
  if (recommendations.length === 0) {
    track("product_empty", { audience });
  }

  recommendations.forEach((recommendation, index) => {
    track(
      "product_impression",
      buildProductImpressionProps(
        recommendation,
        "recommended",
        index + 1,
        audience,
      ),
    );
  });

  alsoLike.forEach((recommendation, index) => {
    track(
      "product_impression",
      buildProductImpressionProps(
        recommendation,
        "also_like",
        index + 1,
        audience,
      ),
    );
  });
}

export function initAnalytics(): void {
  if (!isAnalyticsEnabled() || typeof window === "undefined" || initialized) {
    return;
  }

  posthog.init(posthogToken(), {
    api_host: posthogHost(),
    defaults: "2026-05-30",
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    capture_exceptions: true,
    disable_session_recording: true,
    disable_surveys: true,
    person_profiles: "identified_only",
  });
  registerAllowlistedUtmParams(window.location.search);
  initialized = true;
}
